import { FC } from 'react'

import styled from 'styled-components'
import { useDisclosure } from '@mantine/hooks'
import { Control, Controller } from 'react-hook-form'

import { IconButton } from 'src/Components/Button/IconButton.tsx'
import { Input } from 'src/Components/Input/index.tsx'
import { Select } from 'src/Components/Select'
import { colors, device } from 'src/Helpers/constants.ts'
import IconCirclePlus from 'src/Assets/addButton.svg?react'
import { useGetAllClientsQuery } from 'src/Redux/api/clientsApi.ts'
import {
  Role,
  useGetAllManagersQuery,
  useGetProfileQuery,
} from 'src/Redux/api/authApi.ts'

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 17px;
`

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  position: relative;

  @media ${device.tabletMax} {
    width: 100%;
  }
`

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  margin-top: 5px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`

const StyledSelect = styled(Select)`
  width: 100%;
`

const StyledInput = styled(Input)`
  width: 500px;
  input {
    border: 1px solid ${colors.secondGray};
    color: ${colors.thirdBlue};
    --input-placeholder-color: ${colors.thirdBlue};
  }

  @media ${device.tabletMax} {
    width: 100%;
  }
`

const StyledButton = styled(IconButton)`
  svg {
    path {
      fill: ${colors.fourthBlue};
    }
  }
`

export interface SearchOffersFormData {
  searchTerm?: string
  selectedClientId?: string
  selectedManagerId?: string
}

interface SearchBlockProps {
  onAddNewOffer: () => void
  control: Control<SearchOffersFormData>
}

export const SearchBlock: FC<SearchBlockProps> = ({
  onAddNewOffer,
  control,
}) => {
  const [opened, { toggle }] = useDisclosure(false)
  const { data: user } = useGetProfileQuery()
  const { data: clientsResponse } = useGetAllClientsQuery({ all: true })
  const { data: managersResponse } = useGetAllManagersQuery()

  const clientSelectData = (() => {
    if (!clientsResponse?.data) return []

    const uniqueClientsMap = new Map()

    clientsResponse.data.forEach((client) => {
      uniqueClientsMap.set(client.id, {
        value: client.id.toString(),
        label: client.name,
      })
    })

    return Array.from(uniqueClientsMap.values())
  })()

  const managerSelectData = (() => {
    if (!managersResponse?.data) return []

    return managersResponse.data.map((manager) => {
      const displayName =
        [manager.firstName, manager.lastName].filter(Boolean).join(' ') ||
        manager.email

      return {
        value: manager.id.toString(),
        label: displayName,
      }
    })
  })()

  return (
    <ControlsContainer>
      <StyledButton
        onClick={onAddNewOffer}
        tooltiptext={'Создать новое КП'}
        data-testid='add-offer'
      >
        <IconCirclePlus />
      </StyledButton>

      <FiltersContainer>
        <Controller
          name='searchTerm'
          control={control}
          render={({ field }) => (
            <StyledInput
              placeholder={'Поиск + фильтр'}
              {...field}
              onClick={toggle}
              data-testid='search-offer'
            />
          )}
        />

        {opened && (
          <DropdownContainer>
            {user?.role !== Role.SALES && (
              <Controller
                name='selectedManagerId'
                control={control}
                render={({ field }) => (
                  <StyledSelect
                    placeholder='Менеджер'
                    data={managerSelectData}
                    {...field}
                    onChange={(value) => {
                      field.onChange(value ? Number(value) : undefined)
                    }}
                    clearable
                    data-testid='filter-manager'
                  />
                )}
              />
            )}
            <Controller
              name='selectedClientId'
              control={control}
              render={({ field }) => (
                <StyledSelect
                  placeholder='Клиент'
                  data={clientSelectData}
                  {...field}
                  onChange={(value) => {
                    field.onChange(value ? Number(value) : undefined)
                  }}
                  clearable
                  data-testid='filter-client'
                />
              )}
            />
          </DropdownContainer>
        )}
      </FiltersContainer>
    </ControlsContainer>
  )
}
